/**
 * Servicio para manejar la API de horarios bloqueados
 * Proporciona funciones para gestionar bloques de tiempo no disponibles para citas
 */

import apiClient from './apiClient';
import { API_ENDPOINTS } from '@/config/api';

/**
 * Obtiene todos los horarios bloqueados
 * @returns {Promise<Array>} - Lista de horarios bloqueados
 */
export const getHorariosBloqueados = async () => {
  try {
    //console.log('🚫 [HorariosBloqueadosService] Obteniendo lista de horarios bloqueados...');
    
    const response = await apiClient.get(API_ENDPOINTS.HORARIOS_BLOQUEADOS.BASE);
    
    if (response.data) {
      const horariosData = response.data.data || response.data.horarios_bloqueados || response.data;
      //console.log('✅ [HorariosBloqueadosService] Horarios bloqueados obtenidos:', horariosData.length);
      return horariosData;
    } else {
      console.warn('⚠️ [HorariosBloqueadosService] No se encontraron horarios bloqueados');
      return [];
    }
  } catch (error) {
    console.error('❌ [HorariosBloqueadosService] Error al obtener horarios bloqueados:', error);
    throw error;
  }
};

/**
 * Obtiene un horario bloqueado por ID
 * @param {number} horarioBloqueadoId - ID del horario bloqueado
 * @returns {Promise<Object>} - Datos del horario bloqueado
 */
export const getHorarioBloqueadoById = async (horarioBloqueadoId) => {
  try {
    //console.log('🚫 [HorariosBloqueadosService] Obteniendo horario bloqueado por ID:', horarioBloqueadoId);
    
    const response = await apiClient.get(API_ENDPOINTS.HORARIOS_BLOQUEADOS.BY_ID(horarioBloqueadoId));
    
    if (response.data) {
      const horarioData = response.data.data || response.data.horario_bloqueado || response.data;
      //console.log('✅ [HorariosBloqueadosService] Horario bloqueado obtenido:', horarioData);
      return horarioData;
    } else {
      throw new Error('Horario bloqueado no encontrado');
    }
  } catch (error) {
    console.error('❌ [HorariosBloqueadosService] Error al obtener horario bloqueado:', error);
    throw error;
  }
};

/**
 * Obtiene horarios bloqueados por sucursal
 * @param {number} sucursalId - ID de la sucursal
 * @returns {Promise<Array>} - Lista de horarios bloqueados de la sucursal
 */
export const getHorariosBloqueadosBySucursal = async (sucursalId) => {
  try {
    //console.log('🚫 [HorariosBloqueadosService] Obteniendo horarios bloqueados por sucursal:', sucursalId);
    
    const response = await apiClient.get(API_ENDPOINTS.HORARIOS_BLOQUEADOS.BY_SUCURSAL(sucursalId));
    
    if (response.data) {
      const horariosData = response.data.data || response.data.horarios_bloqueados || response.data;
      //console.log('✅ [HorariosBloqueadosService] Horarios bloqueados de sucursal obtenidos:', horariosData.length);
      return horariosData;
    } else {
      return [];
    }
  } catch (error) {
    console.error('❌ [HorariosBloqueadosService] Error al obtener horarios bloqueados por sucursal:', error);
    throw error;
  }
};

/**
 * Obtiene horarios bloqueados para una fecha específica
 * @param {string} fecha - Fecha a consultar (YYYY-MM-DD)
 * @param {number} sucursalId - ID de la sucursal (opcional)
 * @returns {Promise<Array>} - Lista de horarios bloqueados para la fecha
 */
export const getHorariosBloqueadosByFecha = async (fecha, sucursalId = null) => {
  try {
    //console.log('🚫 [HorariosBloqueadosService] Obteniendo horarios bloqueados por fecha:', { fecha, sucursalId });
    
    const params = sucursalId ? { sucursal_id: sucursalId } : {};
    const response = await apiClient.get(API_ENDPOINTS.HORARIOS_BLOQUEADOS.BY_DATE(fecha), {
      params
    });
    
    if (response.data) {
      const horariosData = response.data.data || response.data.horarios_bloqueados || response.data;
      //console.log('✅ [HorariosBloqueadosService] Horarios bloqueados por fecha obtenidos:', horariosData.length);
      return horariosData;
    } else {
      return [];
    }
  } catch (error) {
    console.error('❌ [HorariosBloqueadosService] Error al obtener horarios bloqueados por fecha:', error);
    throw error;
  }
};

/**
 * Obtiene horarios bloqueados en un rango de fechas
 * @param {string} fechaInicio - Fecha de inicio (YYYY-MM-DD)
 * @param {string} fechaFin - Fecha de fin (YYYY-MM-DD)
 * @param {number} sucursalId - ID de la sucursal (opcional)
 * @returns {Promise<Array>} - Lista de horarios bloqueados en el rango
 */
export const getHorariosBloqueadosByDateRange = async (fechaInicio, fechaFin, sucursalId = null) => {
  try {
    /*console.log('🚫 [HorariosBloqueadosService] Obteniendo horarios bloqueados por rango:', { 
      fechaInicio, 
      fechaFin, 
      sucursalId 
    });*/
    
    const params = {
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin
    };
    
    if (sucursalId) {
      params.sucursal_id = sucursalId;
    }
    
    const response = await apiClient.get(API_ENDPOINTS.HORARIOS_BLOQUEADOS.BY_DATE_RANGE, {
      params
    });
    
    if (response.data) {
      const horariosData = response.data.data || response.data.horarios_bloqueados || response.data;
      //console.log('✅ [HorariosBloqueadosService] Horarios bloqueados en rango obtenidos:', horariosData.length);
      return horariosData;
    } else {
      return [];
    }
  } catch (error) {
    console.error('❌ [HorariosBloqueadosService] Error al obtener horarios bloqueados por rango:', error);
    throw error;
  }
};

/**
 * Crea un nuevo horario bloqueado
 * @param {Object} horarioBloqueadoData - Datos del horario bloqueado
 * @returns {Promise<Object>} - Horario bloqueado creado
 */
export const createHorarioBloqueado = async (horarioBloqueadoData) => {
  try {
    //console.log('🚫 [HorariosBloqueadosService] Creando horario bloqueado:', horarioBloqueadoData);
    
    const response = await apiClient.post(API_ENDPOINTS.HORARIOS_BLOQUEADOS.BASE, horarioBloqueadoData);
    
    if (response.data) {
      const horarioBloqueado = response.data.data || response.data.horario_bloqueado || response.data;
      //console.log('✅ [HorariosBloqueadosService] Horario bloqueado creado:', horarioBloqueado);
      return horarioBloqueado;
    } else {
      throw new Error('Error al crear el horario bloqueado');
    }
  } catch (error) {
    console.error('❌ [HorariosBloqueadosService] Error al crear horario bloqueado:', error);
    throw error;
  }
};

/**
 * Actualiza un horario bloqueado existente
 * @param {number} horarioBloqueadoId - ID del horario bloqueado
 * @param {Object} horarioBloqueadoData - Datos actualizados del horario bloqueado
 * @returns {Promise<Object>} - Horario bloqueado actualizado
 */
export const updateHorarioBloqueado = async (horarioBloqueadoId, horarioBloqueadoData) => {
  try {
    /*console.log('🚫 [HorariosBloqueadosService] Actualizando horario bloqueado:', { 
      horarioBloqueadoId, 
      horarioBloqueadoData 
    });*/
    
    const response = await apiClient.put(
      API_ENDPOINTS.HORARIOS_BLOQUEADOS.BY_ID(horarioBloqueadoId), 
      horarioBloqueadoData
    );
    
    if (response.data) {
      const horarioBloqueado = response.data.data || response.data.horario_bloqueado || response.data;
      //console.log('✅ [HorariosBloqueadosService] Horario bloqueado actualizado:', horarioBloqueado);
      return horarioBloqueado;
    } else {
      throw new Error('Error al actualizar el horario bloqueado');
    }
  } catch (error) {
    console.error('❌ [HorariosBloqueadosService] Error al actualizar horario bloqueado:', error);
    throw error;
  }
};

/**
 * Elimina un horario bloqueado
 * @param {number} horarioBloqueadoId - ID del horario bloqueado
 * @returns {Promise<boolean>} - true si se eliminó correctamente
 */
export const deleteHorarioBloqueado = async (horarioBloqueadoId) => {
  try {
    //console.log('🚫 [HorariosBloqueadosService] Eliminando horario bloqueado:', horarioBloqueadoId);
    
    const response = await apiClient.delete(API_ENDPOINTS.HORARIOS_BLOQUEADOS.BY_ID(horarioBloqueadoId));
    
    //console.log('✅ [HorariosBloqueadosService] Horario bloqueado eliminado correctamente');
    return true;
  } catch (error) {
    console.error('❌ [HorariosBloqueadosService] Error al eliminar horario bloqueado:', error);
    throw error;
  }
};

/**
 * Verifica si un horario específico está bloqueado
 * @param {string} fecha - Fecha a verificar (YYYY-MM-DD)
 * @param {string} hora - Hora a verificar (HH:mm)
 * @param {number} sucursalId - ID de la sucursal (opcional)
 * @returns {Promise<boolean>} - true si el horario está bloqueado
 */
export const isHorarioBloqueado = async (fecha, hora, sucursalId = null) => {
  try {
    const horariosBloqueados = await getHorariosBloqueadosByFecha(fecha, sucursalId);
    
    return horariosBloqueados.some(horario => {
      const horaInicio = horario.hora_inicio;
      const horaFin = horario.hora_fin;
      
      // Verificar si la hora está dentro del rango bloqueado
      return hora >= horaInicio && hora <= horaFin;
    });
  } catch (error) {
    console.error('❌ [HorariosBloqueadosService] Error al verificar horario bloqueado:', error);
    return false;
  }
};

/**
 * Obtiene los horarios disponibles para una fecha, excluyendo los bloqueados
 * @param {string} fecha - Fecha a consultar (YYYY-MM-DD)
 * @param {Array} horariosBase - Horarios base disponibles
 * @param {number} sucursalId - ID de la sucursal (opcional)
 * @returns {Promise<Array>} - Lista de horarios disponibles
 */
export const getHorariosDisponibles = async (fecha, horariosBase, sucursalId = null) => {
  try {
    const horariosBloqueados = await getHorariosBloqueadosByFecha(fecha, sucursalId);
    
    if (horariosBloqueados.length === 0) {
      return horariosBase;
    }
    
    // Filtrar horarios base excluyendo los bloqueados
    const horariosDisponibles = horariosBase.filter(hora => {
      return !horariosBloqueados.some(horarioBloqueado => {
        const horaInicio = horarioBloqueado.hora_inicio;
        const horaFin = horarioBloqueado.hora_fin;
        
        // Verificar si la hora está dentro del rango bloqueado
        return hora >= horaInicio && hora <= horaFin;
      });
    });
    
    return horariosDisponibles;
  } catch (error) {
    console.error('❌ [HorariosBloqueadosService] Error al obtener horarios disponibles:', error);
    return horariosBase; // Retornar horarios base en caso de error
  }
};

/**
 * Crea múltiples horarios bloqueados para un rango de fechas
 * @param {string} fechaInicio - Fecha de inicio (YYYY-MM-DD)
 * @param {string} fechaFin - Fecha de fin (YYYY-MM-DD)
 * @param {string} horaInicio - Hora de inicio (HH:mm)
 * @param {string} horaFin - Hora de fin (HH:mm)
 * @param {string} motivo - Motivo del bloqueo
 * @param {number} sucursalId - ID de la sucursal
 * @returns {Promise<Array>} - Lista de horarios bloqueados creados
 */
export const createHorariosBloqueadosEnRango = async (fechaInicio, fechaFin, horaInicio, horaFin, motivo, sucursalId) => {
  try {
    /*console.log('🚫 [HorariosBloqueadosService] Creando horarios bloqueados en rango:', {
      fechaInicio,
      fechaFin,
      horaInicio,
      horaFin,
      motivo,
      sucursalId
    });*/
    
    const horariosCreados = [];
    const fechaActual = new Date(fechaInicio);
    const fechaFinal = new Date(fechaFin);
    
    while (fechaActual <= fechaFinal) {
      const fechaStr = fechaActual.toISOString().split('T')[0];
      
      const horarioBloqueadoData = {
        fecha: fechaStr,
        hora_inicio: horaInicio,
        hora_fin: horaFin,
        motivo: motivo,
        sucursal_id: sucursalId
      };
      
      const horarioCreado = await createHorarioBloqueado(horarioBloqueadoData);
      horariosCreados.push(horarioCreado);
      
      // Avanzar al siguiente día
      fechaActual.setDate(fechaActual.getDate() + 1);
    }
    
    //console.log('✅ [HorariosBloqueadosService] Horarios bloqueados en rango creados:', horariosCreados.length);
    return horariosCreados;
  } catch (error) {
    console.error('❌ [HorariosBloqueadosService] Error al crear horarios bloqueados en rango:', error);
    throw error;
  }
};